import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { prefersReducedMotion } from '@/lib/gsap'

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
    vUv = aPos * 0.5 + 0.5;
    gl_Position = vec4(aPos, 0.0, 1.0);
}
`

/* Cursor ripple + ambient warp + noise dissolve between two textures.
   This is the effect the reference uses on its hero and project imagery: the
   picture bends toward the pointer, splits its channels slightly at the
   leading edge, and tears into the next frame through a noise threshold
   rather than a plain crossfade. */
const FRAG = `
precision highp float;

varying vec2 vUv;

uniform sampler2D uTex0;
uniform sampler2D uTex1;
uniform vec2 uRes;
uniform vec2 uSize0;
uniform vec2 uSize1;
uniform vec2 uMouse;
uniform float uHover;
uniform float uTime;
uniform float uProgress;
uniform float uIntensity;

vec2 coverUv(vec2 uv, vec2 res, vec2 size) {
    float rRes = res.x / max(res.y, 1.0);
    float rSize = size.x / max(size.y, 1.0);
    vec2 s = rRes > rSize ? vec2(1.0, rSize / rRes) : vec2(rRes / rSize, 1.0);
    return (uv - 0.5) * s + 0.5;
}

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 3; i++) {
        v += a * noise(p);
        p *= 2.03;
        a *= 0.5;
    }
    return v;
}

/* Quintic ease, flat at both ends so a slice starts and lands without a kick. */
float ease(float t) {
    return t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
}

void main() {
    vec2 uv = vUv;
    float aspect = uRes.x / max(uRes.y, 1.0);
    vec2 skew = vec2(aspect, 1.0);

    float d = distance(uv * skew, uMouse * skew);
    vec2 dir = normalize(uv - uMouse + vec2(0.0001));

    float ring = sin(d * 24.0 - uTime * 3.0) * exp(-d * 5.2);
    vec2 hover = dir * ring * 0.03 * uIntensity * uHover;
    hover += (vec2(noise(uv * 3.0 + uTime * 0.1)) - 0.5) * 0.01 * uIntensity;

    /* THE SWITCH.
       The frame is cut into horizontal slices. Each one is released on its own
       delay and slides out sideways, alternating direction, while the incoming
       frame is dragged in behind it from the opposite edge. A slow flow field
       shears the slices so the edge is liquid rather than ruler-straight, and
       the channels split hardest at the moment a slice is moving fastest. A
       plain crossfade this is not. */
    float bands = 11.0;
    float bi = floor(uv.y * bands);
    float seed = hash(vec2(bi * 1.73, 3.1));

    float delay = seed * 0.42;
    float local = clamp((uProgress - delay) / 0.58, 0.0, 1.0);
    float e = ease(local);

    float side = mod(bi, 2.0) < 0.5 ? 1.0 : -1.0;
    float flow = fbm(vec2(uv.x * 2.4, bi * 0.7 + uTime * 0.04)) - 0.5;
    float travel = 0.20 + seed * 0.12;

    vec2 out0 = vec2(side * e * travel + flow * 0.05 * e, 0.0);
    vec2 in1 = vec2(-side * (1.0 - e) * travel + flow * 0.05 * (1.0 - e), 0.0);

    /* Peaks mid-slice, so the shear and the seam light track the movement. */
    float edge = sin(e * 3.14159265);

    vec2 uv0 = coverUv(uv + hover + out0, uRes, uSize0);
    vec2 uv1 = coverUv(uv + hover + in1, uRes, uSize1);

    /* Counter-scale: the outgoing frame swells as it leaves, the incoming one
       settles down onto its own scale. */
    uv0 = (uv0 - 0.5) / (1.0 + 0.07 * e) + 0.5;
    uv1 = (uv1 - 0.5) / (1.0 + 0.09 * (1.0 - e)) + 0.5;

    float split = (0.0035 * uHover + 0.022 * edge) * uIntensity;
    vec2 sdir = vec2(side, 0.0);

    vec3 a = vec3(
        texture2D(uTex0, clamp(uv0 + sdir * split, 0.0, 1.0)).r,
        texture2D(uTex0, clamp(uv0, 0.0, 1.0)).g,
        texture2D(uTex0, clamp(uv0 - sdir * split, 0.0, 1.0)).b
    );

    vec3 b = vec3(
        texture2D(uTex1, clamp(uv1 + sdir * split, 0.0, 1.0)).r,
        texture2D(uTex1, clamp(uv1, 0.0, 1.0)).g,
        texture2D(uTex1, clamp(uv1 - sdir * split, 0.0, 1.0)).b
    );

    /* A tight handover inside each slice, so it reads as a cut being carried
       across rather than one picture dissolving into another. */
    vec3 col = mix(a, b, smoothstep(0.40, 0.60, e));

    /* The accent flares along the seam at the crossing point. Squared by hand:
       pow() with a negative base is undefined in GLSL ES. */
    float t = (e - 0.5) * 5.5;
    float seam = exp(-t * t);
    col += vec3(0.26, 0.38, 1.0) * seam * 0.09;

    gl_FragColor = vec4(col, 1.0);
}
`

const compile = (gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type)

    if (!shader) {
        return null
    }

    gl.shaderSource(shader, source)
    gl.compileShader(shader)

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader)

        return null
    }

    return shader
}

const makeTexture = (gl: WebGLRenderingContext, image: HTMLImageElement): WebGLTexture | null => {
    const texture = gl.createTexture()

    if (!texture) {
        return null
    }

    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

    return texture
}

interface LiquidImageProps {
    sources: string[]
    index?: number
    alt: string
    className?: string
    imageClassName?: string
    intensity?: number
    /* Transition length in seconds when `index` changes. */
    transition?: number
    priority?: boolean
}

export const LiquidImage: FC<LiquidImageProps> = ({
    sources,
    index = 0,
    alt,
    className,
    imageClassName,
    intensity = 1,
    transition = 1.1,
    priority = false
}): ReactNode => {
    const wrapRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [live, setLive] = useState(false)
    const indexRef = useRef(index)

    /* Kept in refs because the render loop reads them every frame and must not
       re-subscribe when React re-renders. */
    const target = useRef({ mx: 0.5, my: 0.5, hover: 0, progress: 0 })
    const current = useRef({ mx: 0.5, my: 0.5, hover: 0, progress: 0 })
    const pair = useRef({ from: index, to: index })
    const api = useRef<{ swap: (next: number) => void } | null>(null)

    useEffect(() => {
        const wrap = wrapRef.current
        const canvas = canvasRef.current

        if (!wrap || !canvas || sources.length === 0 || prefersReducedMotion()) {
            return
        }

        const gl = (canvas.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false })
            ?? canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null

        if (!gl) {
            return
        }

        const vs = compile(gl, gl.VERTEX_SHADER, VERT)
        const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG)
        const program = gl.createProgram()

        if (!vs || !fs || !program) {
            return
        }

        gl.attachShader(program, vs)
        gl.attachShader(program, fs)
        gl.linkProgram(program)

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            return
        }

        gl.useProgram(program)

        const buffer = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)

        const aPos = gl.getAttribLocation(program, 'aPos')
        gl.enableVertexAttribArray(aPos)
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)

        const u = {
            tex0: gl.getUniformLocation(program, 'uTex0'),
            tex1: gl.getUniformLocation(program, 'uTex1'),
            res: gl.getUniformLocation(program, 'uRes'),
            size0: gl.getUniformLocation(program, 'uSize0'),
            size1: gl.getUniformLocation(program, 'uSize1'),
            mouse: gl.getUniformLocation(program, 'uMouse'),
            hover: gl.getUniformLocation(program, 'uHover'),
            time: gl.getUniformLocation(program, 'uTime'),
            progress: gl.getUniformLocation(program, 'uProgress'),
            intensity: gl.getUniformLocation(program, 'uIntensity')
        }

        const textures: (WebGLTexture | null)[] = sources.map(() => null)
        const sizes: [number, number][] = sources.map(() => [1, 1])
        let disposed = false
        let frame = 0
        let inView = true
        let ready = false

        const load = (src: string, slot: number): Promise<void> => {
            return new Promise((resolve) => {
                const image = new Image()
                image.crossOrigin = 'anonymous'

                image.onload = () => {
                    if (disposed) {
                        resolve()

                        return
                    }

                    textures[slot] = makeTexture(gl, image)
                    sizes[slot] = [image.naturalWidth, image.naturalHeight]
                    resolve()
                }

                image.onerror = () => resolve()
                image.src = src
            })
        }

        const resize = (): void => {
            const rect = wrap.getBoundingClientRect()
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            const w = Math.max(1, Math.round(rect.width * dpr))
            const h = Math.max(1, Math.round(rect.height * dpr))

            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w
                canvas.height = h
                gl.viewport(0, 0, w, h)
            }
        }

        const observer = new IntersectionObserver((entries) => {
            inView = entries[0]?.isIntersecting ?? true
        }, { rootMargin: '150px' })

        observer.observe(wrap)

        const resizeObserver = new ResizeObserver(resize)
        resizeObserver.observe(wrap)

        const onMove = (event: PointerEvent): void => {
            const rect = wrap.getBoundingClientRect()
            target.current.mx = (event.clientX - rect.left) / rect.width
            target.current.my = 1 - (event.clientY - rect.top) / rect.height
        }

        const onEnter = (): void => {
            target.current.hover = 1
        }

        const onLeave = (): void => {
            target.current.hover = 0
        }

        wrap.addEventListener('pointermove', onMove, { passive: true })
        wrap.addEventListener('pointerenter', onEnter)
        wrap.addEventListener('pointerleave', onLeave)

        api.current = {
            swap: (next: number) => {
                if (next === pair.current.to) {
                    return
                }

                pair.current = { from: pair.current.to, to: next }
                current.current.progress = 0
                target.current.progress = 1
            }
        }

        const start = performance.now()

        const render = (now: number): void => {
            if (disposed) {
                return
            }

            frame = requestAnimationFrame(render)

            if (!inView || !ready) {
                return
            }

            resize()

            const c = current.current
            const t = target.current

            c.mx += (t.mx - c.mx) * 0.07
            c.my += (t.my - c.my) * 0.07
            c.hover += (t.hover - c.hover) * 0.06

            if (t.progress > c.progress) {
                c.progress = Math.min(1, c.progress + (1 / 60) / transition)

                if (c.progress >= 1) {
                    pair.current = { from: pair.current.to, to: pair.current.to }
                    c.progress = 0
                    t.progress = 0
                }
            }

            const fromSlot = pair.current.from
            const toSlot = pair.current.to

            gl.activeTexture(gl.TEXTURE0)
            gl.bindTexture(gl.TEXTURE_2D, textures[fromSlot] ?? textures[0])
            gl.uniform1i(u.tex0, 0)

            gl.activeTexture(gl.TEXTURE1)
            gl.bindTexture(gl.TEXTURE_2D, textures[toSlot] ?? textures[0])
            gl.uniform1i(u.tex1, 1)

            gl.uniform2f(u.res, canvas.width, canvas.height)
            gl.uniform2f(u.size0, sizes[fromSlot][0], sizes[fromSlot][1])
            gl.uniform2f(u.size1, sizes[toSlot][0], sizes[toSlot][1])
            gl.uniform2f(u.mouse, c.mx, c.my)
            gl.uniform1f(u.hover, c.hover)
            gl.uniform1f(u.time, (now - start) / 1000)
            gl.uniform1f(u.progress, c.progress)
            gl.uniform1f(u.intensity, intensity)

            gl.drawArrays(gl.TRIANGLES, 0, 3)
        }

        Promise.all(sources.map((src, slot) => load(src, slot))).then(() => {
            if (disposed || !textures.some(Boolean)) {
                return
            }

            ready = true
            resize()
            setLive(true)
        })

        frame = requestAnimationFrame(render)

        return () => {
            disposed = true
            cancelAnimationFrame(frame)
            observer.disconnect()
            resizeObserver.disconnect()
            wrap.removeEventListener('pointermove', onMove)
            wrap.removeEventListener('pointerenter', onEnter)
            wrap.removeEventListener('pointerleave', onLeave)
            textures.forEach((texture) => texture && gl.deleteTexture(texture))
            gl.deleteBuffer(buffer)
            gl.deleteProgram(program)
            gl.deleteShader(vs)
            gl.deleteShader(fs)
            api.current = null
            setLive(false)
        }
    }, [sources.join('|'), intensity, transition])

    useEffect(() => {
        if (index !== indexRef.current) {
            indexRef.current = index
            api.current?.swap(index)
        }
    }, [index])

    return (
        <div ref={wrapRef} className={cn('relative overflow-hidden', className)}>
            {/* The real element for search, screen readers and no-WebGL clients.
                It stays in the DOM and simply hands over once the canvas is up. */}
            <img
                src={sources[index] ?? sources[0]}
                alt={alt}
                loading={priority ? 'eager' : 'lazy'}
                decoding='async'
                className={cn(
                    'absolute inset-0 size-full object-cover transition-opacity duration-500',
                    live ? 'opacity-0' : 'opacity-100',
                    imageClassName
                )}
            />

            <canvas
                ref={canvasRef}
                aria-hidden='true'
                className={cn(
                    'absolute inset-0 size-full transition-opacity duration-500',
                    live ? 'opacity-100' : 'opacity-0'
                )}
            />
        </div>
    )
}
